import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mountain, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { DISCLAIMER } from "@/lib/constants";
import api from "@/lib/api";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (password !== confirm) { toast.error("As senhas não coincidem."); return; }
        if (password.length < 6) { toast.error("A senha deve ter no mínimo 6 caracteres."); return; }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, password });
            toast.success("Senha redefinida! Faça login com a nova senha.");
            navigate("/login");
        } catch (err) {
            const d = err.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Não foi possível redefinir a senha.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
            <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm p-8">
                <Link to="/" className="flex items-center gap-2 font-heading font-black text-xl text-petroleum mb-6" data-testid="reset-logo">
                    <Mountain className="w-7 h-7 text-mineral" /> GEOMINE MANAGER
                </Link>
                <h1 className="font-heading text-2xl font-black text-slate-900">Criar nova senha</h1>
                {!token ? (
                    <p className="text-red-600 mt-3 text-sm" data-testid="reset-no-token">Link inválido. Solicite uma nova redefinição em "Esqueci minha senha".</p>
                ) : (
                    <>
                        <p className="text-slate-500 mt-2">Defina uma nova senha para sua conta.</p>
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <label className="text-sm font-semibold text-slate-700">Nova senha</label>
                                <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input"
                                    className="mt-1 w-full rounded-md border-2 border-slate-200 focus:border-petroleum outline-none px-4 py-2.5 transition-colors" placeholder="Mínimo 6 caracteres" />
                            </div>
                            <div>
                                <label className="text-sm font-semibold text-slate-700">Confirmar senha</label>
                                <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input"
                                    className="mt-1 w-full rounded-md border-2 border-slate-200 focus:border-petroleum outline-none px-4 py-2.5 transition-colors" placeholder="Repita a senha" />
                            </div>
                            <button type="submit" disabled={loading} data-testid="reset-submit-button"
                                className="w-full flex items-center justify-center gap-2 bg-petroleum hover:bg-petroleumHover text-white rounded-md px-6 py-3 font-semibold transition-colors disabled:opacity-60">
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Redefinir senha
                            </button>
                        </form>
                    </>
                )}
                <div className="mt-6 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
                    <ShieldCheck className="w-4 h-4 text-petroleum shrink-0 mt-0.5" />
                    <span>Por segurança, ao redefinir a senha suas sessões ativas são encerradas.</span>
                </div>
                <p className="mt-6 text-xs text-slate-400 leading-relaxed">{DISCLAIMER}</p>
            </div>
        </div>
    );
}
